// @flow

import { action, computed, observable } from "mobx";
import Cookies from "js-cookie";

import Store from "app/stores/Store";
import AppearanceStore from "app/stores/AppearanceStore";
import TimelineItemStore from "app/stores/TimelineItemStore";
import UserStore from "app/stores/UserStore";

import VideoService from "app/services/VideoService";

import Tag from "app/models/Tag";
import Video from "app/models/Video";

class VideoStore extends Store {
    @observable video: Video;
    @observable history: Video[] = [];
    @observable tags: Tag[] = [];
    @observable deleting: boolean = false;
    @observable relatedTimelines: Video[] = [];
    @observable lastSaved: Date;
    @observable videos: Video[];

    @action.bound setVideos(videos: Video[]): void {
        this.videos = videos;
    }

    @action.bound updateVideo(video: Video): void {
        this.videos = this.videos.map(current => {
            if (current.uid === video.uid) {
                return video;
            }
            return current;
        });
    }


    @action setVideo(video: Video): void {
        this.video = video;
    }

    @action setHistory(history: Video[]): void {
        this.history = history;
    }

    @action setTags(tags: Tag[]): void {
        this.tags = tags;
    }

    @action setRelatedTimelines(videos: Video[]) {
        this.relatedTimelines = videos;
    }

    @action.bound updateLastSaved() {
        this.lastSaved = new Date();
    }

    requireAuthentication(): boolean {
        if (UserStore.user) return false;

        window.location.href = `/sign-up?redirect_url=${window.location.href}`;
        return true;
    }

    @action.bound publish(): Promise<*> {
        if (this.requireAuthentication()) return Promise.reject();

        let url =
            window.location.origin +
            "/watch?tlv=" +
            this.video.uid +
            "&r=" +
            this.video.version;
        Cookies.set("share-video", true);

        const card_count = TimelineItemStore.items.length;
        if (!card_count) {
            AppearanceStore.notifyError(
                "Can't publish a video without timeline items"
            );
            return Promise.reject();
        }

        const version = this.video.version;
        this.track("published-video", { url, card_count, version });

        return VideoService.publish(this.video.uid).then(() => {
            Cookies.set("publish-message", true);
            window.location.href = url;
        });
    }

    @action.bound report(message: string): Promise<*> {
        return VideoService.report(this.video.uid, message).then(
            ({ data }) => {
                AppearanceStore.notifySuccess(data.message);
            },
            ({ response }: any) => {
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    @action.bound delete(): Promise<*> {
        const url =
            window.location.origin +
            "/watch?tlv=" +
            this.video.uid +
            "&r=" +
            this.video.version;
        const title = this.video.title;
        const created_at = this.video.created_at;
        const deleted_at = new Date();
        this.track("deleted-video", { url, title, created_at, deleted_at });

        return VideoService.delete(this.video.uid).then(
            ({ data }) => {
                AppearanceStore.notifySuccess(data.message);
            },
            ({ response }: any) => {
                AppearanceStore.notifyError(response.data.message);
            }
        );
    }

    /*
     * History
     */

    @computed get lastVersionVideo(): Video {
        return this.history[this.history.length - 1];
    }

    @computed get lastPublishedVersion(): ?number {
        const video = this.history
            .reverse()
            .find(version => version.isPublished);
        return video ? video.version : null;
    }

    @computed get isLastVersion(): boolean {
        return this.video.version === this.lastVersionVideo.version;
    }

    /*
     * Editing
     */

    @computed get isOwner(): boolean {
        if (!this.video.created_by && this.video.created_by_guest) {
            return true;
        }

        return !!(
            UserStore.user && UserStore.user.id === this.video.created_by
        );
    }

    @computed get isEditable(): boolean {
        return (
            !AppearanceStore.isMobile &&
            this.isOwner &&
            !this.video.isDeleted &&
            !this.video.isPublished &&
            this.isLastVersion
        );
    }

    @computed get isPublishable(): boolean {
        return this.isEditable && !!TimelineItemStore.items.length;
    }

    @computed get isDraft(): boolean {
        return !this.video.isPublished;
    }

    @computed get shouldAutoPlay(): boolean {
        if (this.hasPreventAutoPlayFlag()) {
            return false;
        }

        const user = UserStore.user;
        if (user && !user.autoplay) {
            return false;
        }

        return true;
    }

    hasPreventAutoPlayFlag(): boolean {
        const url = new URL(window.location.href);
        return url.searchParams.get("preventAutoPlay") === "true"
             || Cookies.get("publish-message"); //Prevent autoplay for a video that has just been published
    }
}

export default new VideoStore();



// WEBPACK FOOTER //
// ./src/app/stores/VideoStore.js